import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ClipboardList } from 'lucide-react';

/* ──────────────────────────────────────────────────────────────────────────
   Game Breakdown — tabbed bottom-sheet modal with FIXED HEIGHT.
   Renders backend coaching insight text VERBATIM, with numeric stats bolded
   for scannability. No paraphrasing, no parsing into raw comparisons.
   ────────────────────────────────────────────────────────────────────────── */

const GREEN = '#22863a';
const RED   = '#c0392b';
const NAVY  = '#0B1824';
const MUTED = '#8FA3B1';

/* Bold any numeric portion (e.g., "58.0%", "12 vs 14", "37.5%") for scannability.
   We split on stat-like tokens; the coaching language is preserved. */
const STAT_PATTERN = /(\d+(?:\.\d+)?\s*%|\d+(?:\.\d+)?\s+vs\s+\d+(?:\.\d+)?(?:\s+rebounds)?|\d+(?:\.\d+)?\s+(?:fouls|rebounds))/gi;

function highlightStats(text) {
  if (!text || typeof text !== 'string') return text;
  const out = [];
  let last = 0;
  let m;
  STAT_PATTERN.lastIndex = 0;
  while ((m = STAT_PATTERN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<strong key={out.length} style={{ color: NAVY, fontWeight: 800 }}>{m[0]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function InsightCard({ text, accent, testid }) {
  return (
    <div
      className="rounded-[14px] p-4 mb-3"
      style={{ background: '#fff', border: `1.5px solid #DDE5EC`, borderLeft: `4px solid ${accent}` }}
      data-testid={testid}
    >
      <p className="text-[15px] leading-[1.55]" style={{ color: '#334155' }}>
        {highlightStats(text)}
      </p>
    </div>
  );
}

function TabPill({ active, label, color, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex-1 h-10 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all active:scale-[.97]"
      style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        background: active ? color : '#F1F5F9',
        color: active ? '#fff' : 'rgba(0,0,0,.45)',
        border: `1.5px solid ${active ? color : 'rgba(0,0,0,0.1)'}`,
      }}
    >
      {label}
    </button>
  );
}

export default function GameBreakdownModal({ open, onOpenChange, analysis, game, loading }) {
  const [tab, setTab] = useState('wins'); // 'wins' | 'opps' | 'focus'
  const opp = game?.opponent_name;
  const date = game?.date_time
    ? new Date(game.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const wins = analysis?.wins || [];
  const opps = analysis?.opportunities || [];
  const focus = analysis?.practiceFocus || analysis?.practice_focus || [];

  /* Body sizing — min-height fits Practice Focus comfortably; max caps at 75vh.
     All three tab panels are stacked in the same grid cell so the body's
     intrinsic height = max(tab content heights). That keeps the sheet stable
     when switching tabs (no height jumping). Overflow scrolls internally. */
  const BODY_MIN = '320px';
  const BODY_MAX = '75vh';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="game-breakdown-modal" className="pb-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[18px] uppercase tracking-[.04em]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: NAVY }}>
            <ClipboardList className="w-5 h-5" /> Game Breakdown
          </DialogTitle>
          {(opp || date) && (
            <DialogDescription className="text-[13px]" style={{ color: MUTED }}>
              {opp ? `vs ${opp}` : ''}{opp && date ? ' · ' : ''}{date}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Tab pills */}
        <div className="flex gap-2 mt-3 mb-4" data-testid="breakdown-tabs">
          <TabPill active={tab === 'wins'}  color={GREEN} label="Wins"           onClick={() => setTab('wins')}  testid="tab-wins"  />
          <TabPill active={tab === 'opps'}  color={RED}   label="Opportunities"  onClick={() => setTab('opps')}  testid="tab-opps"  />
          <TabPill active={tab === 'focus'} color={NAVY}  label="Practice Focus" onClick={() => setTab('focus')} testid="tab-focus" />
        </div>

        {/* Body — bounded by min/max, scrolls internally when overflowing. */}
        <div
          className="overflow-y-auto"
          style={{ minHeight: BODY_MIN, maxHeight: BODY_MAX }}
          data-testid="breakdown-body"
        >
          {loading ? (
            <p className="text-[14px] italic text-center py-6" style={{ color: MUTED }}>Loading breakdown…</p>
          ) : !analysis ? (
            <p className="text-[14px] italic text-center py-6" style={{ color: MUTED }}>Breakdown not available for this game.</p>
          ) : (
            // All three panels stacked in one grid cell — the body sizes to the
            // tallest panel so the modal stays the same height across tabs.
            <div className="grid" style={{ gridTemplateAreas: '"stack"' }}>
              {/* WINS */}
              <div
                style={{ gridArea: 'stack', visibility: tab === 'wins' ? 'visible' : 'hidden' }}
                aria-hidden={tab !== 'wins'}
              >
                {wins.length === 0
                  ? <p className="text-[14px] italic" style={{ color: MUTED }} data-testid="wins-empty">No wins identified for this game.</p>
                  : wins.map((w, i) => <InsightCard key={i} text={w} accent={GREEN} testid={`win-card-${i}`} />)}
              </div>
              {/* OPPORTUNITIES */}
              <div
                style={{ gridArea: 'stack', visibility: tab === 'opps' ? 'visible' : 'hidden' }}
                aria-hidden={tab !== 'opps'}
              >
                {opps.length === 0
                  ? <p className="text-[14px] italic" style={{ color: MUTED }} data-testid="opps-empty">No opportunities identified.</p>
                  : opps.map((o, i) => <InsightCard key={i} text={o} accent={RED} testid={`opp-card-${i}`} />)}
              </div>
              {/* PRACTICE FOCUS */}
              <div
                style={{ gridArea: 'stack', visibility: tab === 'focus' ? 'visible' : 'hidden' }}
                aria-hidden={tab !== 'focus'}
              >
                {focus.length === 0 ? (
                  <p className="text-[14px] italic" style={{ color: MUTED }} data-testid="focus-empty">No practice focus areas for this game.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="practice-focus-tags">
                    {focus.map((tag, i) => {
                      const label = typeof tag === 'string' ? tag : (tag.label || tag.tag);
                      return (
                        <span key={i} className="px-3.5 py-2 rounded-full text-[14px]"
                          style={{
                            fontFamily: "'Barlow Condensed', sans-serif",
                            fontWeight: 700,
                            background: 'rgba(11,24,36,.06)',
                            color: NAVY,
                            border: '1.5px solid rgba(11,24,36,.12)',
                          }}>
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Disclaimer (sits below the fixed-height body — never moves). */}
        <div className="pt-3 mt-3 border-t" style={{ borderColor: '#E2E8F0' }}>
          <p className="text-[11px] italic leading-relaxed" style={{ color: MUTED }} data-testid="breakdown-disclaimer">
            Analysis based on box score stats. Use alongside your own judgment and film review.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
